import React, { createContext, useContext, useEffect, useState } from "react";
import axios from "../util/axiosConfig";

/* ===============================
   타입 정의
=============================== */
type User = {
	id: number;
	providerId: string;
	nickname: string;
	role: string;
	exp: number;
	level: number;
} | null;

type AuthContextType = {
	user: User;
	loading: boolean;
	refreshUser: () => Promise<void>;
	logout: () => void;
};

/* ===============================
   Context 기본값
=============================== */
const AuthContext = createContext<AuthContextType>({
	user: null,
	loading: true,
	refreshUser: async () => {},
	logout: () => {},
});

/* ===============================
   Local Dev 판별
=============================== */
const isLocalDev =
	process.env.NODE_ENV === "development" &&
	(window.location.hostname === "localhost" ||
		window.location.hostname === "127.0.0.1");

/* ===============================
   DEV 가짜 유저 (admin)
=============================== */
const DEV_USER = {
	id: 0,
	providerId: "local-dev",
	nickname: "로컬관리자",
	role: "admin",
	exp: 999999,
	level: 200,
};

/* ===============================
   JWT 만료 체크
=============================== */
function isTokenExpired(token: string) {
	try {
		const payload = JSON.parse(atob(token.split(".")[1]));
		return payload.exp * 1000 < Date.now();
	} catch {
		return true;
	}
}

/* ===============================
   Provider
=============================== */
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
																		  children,
																	  }) => {
	const [user, setUser] = useState<User>(null);
	const [loading, setLoading] = useState(true);

	const refreshUser = async () => {
		setLoading(true);
		const token = localStorage.getItem("accessToken");

		// ⭐ 만료 토큰 사전 제거
		if (!token || isTokenExpired(token)) {
			localStorage.removeItem("accessToken");
			setUser(null);
			setLoading(false);
			return;
		}

		try {
			const res = await axios.get("/api/user/me");
			setUser(res.data);
		} catch (err: any) {
			console.error("유저 정보 불러오기 실패:", err);
			if (err?.response?.status === 401) {
				localStorage.removeItem("accessToken");
			}
			setUser(null);
		} finally {
			setLoading(false);
		}
	};

	const logout = () => {
		localStorage.removeItem("accessToken");
		setUser(null);
		window.location.href = "/";
	};

	useEffect(() => {
		/* ===============================
		   🔥 LOCAL DEV 로그인 우회
		=============================== */
		if (isLocalDev) {
			console.warn("⚠️ LOCAL DEV LOGIN BYPASS ENABLED");
			setUser(DEV_USER as any);
			setLoading(false);
			return;
		}

		/* ===============================
		   기존 로그인 로직
		=============================== */
		refreshUser();
	}, []);

	return (
		<AuthContext.Provider value={{ user, loading, refreshUser, logout }}>
			{children}
		</AuthContext.Provider>
	);
};

/* ===============================
   Hook
=============================== */
export const useAuth = () => useContext(AuthContext);
